import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Message, useToaster, Checkbox } from "rsuite";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import {
  SectionRibbon,
  Button,
  Input,
  Picker,
  GlassCard,
  SpacedGlassCard,
} from "../../../../../ui";

export const GenerateAcquisition = ({ acquisition_config }) => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const initAcquisitionConfig = {
    system_id: acquisition_config?.system_id || id,
    host_ip: acquisition_config?.host_ip || null,
    mmb_ip: acquisition_config?.mmb_ip || null,
    protocal: acquisition_config?.protocal || null,
    debian_server_path: acquisition_config?.debian_server_path || null,
    credentials_group: acquisition_config?.credentials_group || null,
    acquisition_script: acquisition_config?.acquisition_script || null,
    run_group: acquisition_config?.run_group || null,
    host: acquisition_config?.host || null,
    user_id: acquisition_config?.user_id || null,
    acqu_point: acquisition_config?.acqu_point || null,
    file_version: acquisition_config?.file_version || null,
    alt_data_source: acquisition_config?.alt_data_source || null,
    host_path: acquisition_config?.host_path || null,
    cerb_file: acquisition_config?.cerb_file || null,
    vpn: acquisition_config?.vpn || false,
  };

  // Dropdown options
  const protocolOptions = [
    { label: "SSH", value: "ssh" },
    { label: "LFTP", value: "lftp" },
    { label: "RSYNC", value: "rsync" },
  ];

  const acquPointOptions = [
    { label: "MMB", value: "mmb" },
    { label: "Host", value: "host" },
  ];

  const [formValueAcquisition, setFormValueAcquisition] = useState(
    initAcquisitionConfig,
  );
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormValueAcquisition(initAcquisitionConfig);
  };

  const handleInputChange = (field, value) => {
    setFormValueAcquisition((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateConfigEDU = async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/config-acquisition`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          config: formValueAcquisition,
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          Acquisition configuration updated successfully.
        </Message>,
        "topCenter",
      );
    } catch (error) {
      console.error("Failed to update acquisition config:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update acquisition configuration. Please try again.
        </Message>,
        "topCenter",
      );
    }

    setLoading(false);
  };

  const hasChanges =
    JSON.stringify(formValueAcquisition) !==
    JSON.stringify(initAcquisitionConfig);

  return (
    <AcquisitionContainer>
      <SectionRibbon
        title="Acquisition Configuration"
        variant="primary"
        rightContent={
          <ButtonActions>
            <Button variant="subtle" onClick={resetForm} disabled={loading}>
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={updateConfigEDU}
              loading={loading}
            >
              Submit
            </Button>
          </ButtonActions>
        }
      />

      <ConfigurationForm>
        <SpacedGlassCard>
          <FormContent>
            <FormGrid>
              <FormField span="full">
                <Input
                  label="System ID"
                  value={formValueAcquisition.system_id}
                  onChange={(value) => handleInputChange("system_id", value)}
                  disabled
                  placeholder="System ID"
                />
              </FormField>

              <FormField>
                <Input
                  label="Host IP"
                  value={formValueAcquisition.host_ip}
                  onChange={(value) => handleInputChange("host_ip", value)}
                  placeholder="Enter host IP"
                />
              </FormField>

              <FormField>
                <Input
                  label="MMB IP"
                  value={formValueAcquisition.mmb_ip}
                  onChange={(value) => handleInputChange("mmb_ip", value)}
                  placeholder="Enter MMB IP"
                />
              </FormField>

              <FormField>
                <Input
                  label="Host"
                  value={formValueAcquisition.host}
                  onChange={(value) => handleInputChange("host", value)}
                  placeholder="Enter host"
                />
              </FormField>

              <FormField>
                <Input
                  label="User ID"
                  value={formValueAcquisition.user_id}
                  onChange={(value) => handleInputChange("user_id", value)}
                  placeholder="Enter user ID"
                />
              </FormField>

              <FormField>
                <Input
                  label="Host Path"
                  value={formValueAcquisition.host_path}
                  onChange={(value) => handleInputChange("host_path", value)}
                  placeholder="Enter host path"
                />
              </FormField>

              <FormField span="double">
                <Input
                  label="Debian Server Path"
                  value={formValueAcquisition.debian_server_path}
                  onChange={(value) =>
                    handleInputChange("debian_server_path", value)
                  }
                  placeholder="Enter debian server path"
                />
              </FormField>

              <FormField>
                <Input
                  label="Acquisition Script"
                  value={formValueAcquisition.acquisition_script}
                  onChange={(value) =>
                    handleInputChange("acquisition_script", value)
                  }
                  placeholder="Enter acquisition script"
                />
              </FormField>

              <FormField>
                <Picker
                  label="Protocol"
                  data={protocolOptions}
                  value={formValueAcquisition.protocal}
                  onChange={(value) => handleInputChange("protocal", value)}
                  placeholder="Select protocol"
                  searchable={false}
                />
              </FormField>

              <FormField>
                <Picker
                  label="Acqu Point"
                  data={acquPointOptions}
                  value={formValueAcquisition.acqu_point}
                  onChange={(value) => handleInputChange("acqu_point", value)}
                  placeholder="Select acqu point"
                  searchable={false}
                />
              </FormField>

              {/*<FormField>
                <CheckboxField>
                  <CheckboxLabel>VPN</CheckboxLabel>
                  <Checkbox
                    checked={formValueAcquisition.vpn}
                    onChange={(checked) => handleInputChange("vpn", checked)}
                  />
                </CheckboxField>
                </FormField>*/}

              <FormField>
                <Input
                  label="Credentials Group"
                  type="number"
                  value={formValueAcquisition.credentials_group}
                  onChange={(value) =>
                    handleInputChange("credentials_group", value)
                  }
                  placeholder="Enter credentials group"
                  min="0"
                />
              </FormField>

              <FormField>
                <Input
                  label="Run Group"
                  type="number"
                  value={formValueAcquisition.run_group}
                  onChange={(value) => handleInputChange("run_group", value)}
                  placeholder="Enter run group"
                  min="0"
                />
              </FormField>

              <FormField>
                <Input
                  label="File Version"
                  value={formValueAcquisition.file_version}
                  onChange={(value) => handleInputChange("file_version", value)}
                  placeholder="Enter file version"
                />
              </FormField>

              <FormField span="double">
                <Input
                  label="Alt Data Source"
                  value={formValueAcquisition.alt_data_source}
                  onChange={(value) =>
                    handleInputChange("alt_data_source", value)
                  }
                  placeholder="Enter alt data source"
                />
              </FormField>

              <FormField>
                <Input
                  label="Cerb File"
                  value={formValueAcquisition.cerb_file}
                  onChange={(value) => handleInputChange("cerb_file", value)}
                  placeholder="Enter cerb file"
                />
              </FormField>
            </FormGrid>
          </FormContent>
        </SpacedGlassCard>
      </ConfigurationForm>

      <ConfigurationInfo>
        <GlassCard>
          <InfoContent>
            <InfoHeader>
              <InfoTitle>Acquisition Configuration Guidelines</InfoTitle>
            </InfoHeader>

            <InfoGrid>
              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Network Settings</InfoItemTitle>
                  <InfoItemText>
                    Configure host and MMB IP addresses for secure communication
                    between system components.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Protocol Selection</InfoItemTitle>
                  <InfoItemText>
                    Choose appropriate protocol (SSH, LFTP, RSYNC) based on
                    security requirements and data transfer needs.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Security Configuration</InfoItemTitle>
                  <InfoItemText>
                    Configure VPN settings and credentials groups for secure
                    data acquisition and processing.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>
            </InfoGrid>
          </InfoContent>
        </GlassCard>
      </ConfigurationInfo>
    </AcquisitionContainer>
  );
};

// Styled Components
const AcquisitionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ButtonActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ConfigurationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormContent = styled.div`
  padding: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ span }) => {
    if (span === "full") return "grid-column: 1 / -1;";
    if (span === "double") return "grid-column: span 2;";
    return "";
  }}

  @media (max-width: 1200px) {
    ${({ span }) => {
      if (span === "double") return "grid-column: span 2;";
      return "grid-column: span 1;";
    }}
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const CheckboxField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
`;

const ConfigurationInfo = styled.div`
  margin-top: 1rem;
`;

const InfoContent = styled.div`
  padding: 2rem;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const InfoItemIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoItemContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoItemText = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.5;
`;
